import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { Breadcrumb } from './breadcrumb';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'wilson-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BreadcrumbComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(Injector);

  breadcrumbs$!: Observable<Breadcrumb[]>;
  faAngleRight = faAngleRight;

  ngOnInit() {
    this.breadcrumbs$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(this.router),
      distinctUntilChanged(),
      map(() => this.buildBreadCrumb(this.activatedRoute.root)),
    );
  }

  private buildBreadCrumb(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: Breadcrumb[] = [],
  ): Breadcrumb[] {
    const data = route.routeConfig?.data;
    const label = data ? data['breadcrumb'] : '';
    let path = route.routeConfig ? route.routeConfig.path : '';

    const lastRoutePart = path.split('/').pop();
    const isDynamicRoute = lastRoutePart.startsWith(':');

    if (isDynamicRoute && !!route.snapshot) {
      const paramName = lastRoutePart.split(':')[1];
      path = path.replace(lastRoutePart, route.snapshot.params[paramName]);
      //label = route.snapshot.params[paramName];
    }

    const nextUrl = path ? `${url}/${path}` : url;

    const info = data?.['breadcrumbInfo'];
    // A route can override its computed url/queryParams when the breadcrumb
    // trail doesn't mirror route nesting (e.g. a detail route that isn't
    // nested under the list route it should link back to).
    const breadcrumbUrl = data?.['breadcrumbUrl'] as string | undefined;
    const breadcrumbQueryParams = data?.['breadcrumbQueryParams'] as
      | ((injector: Injector) => Params)
      | undefined;

    const breadcrumb: Breadcrumb = {
      label: label,
      url: breadcrumbUrl ?? nextUrl,
      ...(info ? { info } : {}),
      ...(breadcrumbQueryParams
        ? { queryParams: breadcrumbQueryParams(this.injector) }
        : {}),
    };
    // Only adding route with non-empty label
    const newBreadcrumbs = breadcrumb.label
      ? [...breadcrumbs, breadcrumb]
      : [...breadcrumbs];
    if (route.firstChild) {
      //If we are not on our current path yet,
      //there will be more children to look after, to build our breadcumb
      return this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs);
    }

    return newBreadcrumbs;
  }
}
